// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import omit from 'lodash/omit';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import isEmpty from 'lodash/isEmpty';
import toast from 'react-hot-toast';
// TODO: fix import cycle
// eslint-disable-next-line import/no-cycle
import * as componentLibrary from '../../lib/component-lib';
import { startLoading, doneLoading } from '../../lib/loading/actions';
import { positionInParent } from '../../lib/outline-manipulation';
import { PAGE_ROOT } from '../../lib/constants';

export const OUTLINE_RECEIVED = 'OUTLINE_RECEIVED';
export const OUTLINE_UPDATED = 'OUTLINE_UPDATED';

export const COMPONENT_ADDED = 'COMPONENT_ADDED';
export const COMPONENT_UPDATED = 'COMPONENT_UPDATED';
export const COMPONENT_DELETED = 'COMPONENT_DELETED';
export const COMPONENT_SELECTED = 'COMPONENT_SELECTED';

export const EDITOR_CLOSE = 'EDITOR_CLOSE';
export const EDITOR_OPEN = 'EDITOR_OPEN';
export const EDITOR_TAB_SELECT = 'EDITOR_TAB_SELECT';
export const EDITOR_UPDATE_HEIGHT = 'EDITOR_UPDATE_HEIGHT';
export const EDITOR_UPDATE_WIDTH = 'EDITOR_UPDATE_WIDTH';
export const EDITOR_UPDATE_DOCK = 'EDITOR_UPDATE_DOCK';

export const ADD_REPEATABLE = 'ADD_REPEATABLE';
export const MOVE_REPEATABLE = 'MOVE_REPEATABLE';
export const DELETE_REPEATABLE = 'DELETE_REPEATABLE';
export const BUILDER_LOADED = 'BUILDER_LOADED';

export const SHOULD_WATCH_QUERY_PARAMS = 'SHOULD_WATCH_QUERY_PARAMS';

export const ALWAYS_RENDER_IN_BUILDER = 'ALWAYS_RENDER_IN_BUILDER';

export const TOGGLE_BADGE = 'TOGGLE_BADGE';
export const CLOSE_ALL_BADGES = 'CLOSE_ALL_BADGES';

export const START_SORTING = 'START_SORTING';
export const STOP_SORTING = 'STOP_SORTING';
export const MOVE_COMPONENT = 'MOVE_COMPONENT';

export const SET_BUILDER_VERSION = 'SET_BUILDER_VERSION';

// makeId must return a unique ID
// we want it to be an integer for testing purposes
export const makeId = (componentDocument) => {
  const randomInt = Math.floor(Math.random() * Math.floor(1000000));
  return `${randomInt}-${componentDocument.type}`;
};

export const builderLoaded = () => ({ type: BUILDER_LOADED });

export const addComponent = (component, order) => ({
  type: COMPONENT_ADDED,
  component,
  order,
});

export const addRepeatable = (path, repeatable = {}) => ({
  type: ADD_REPEATABLE,
  path,
  repeatable,
});
export const moveRepeatable = (path, fromIndex, toIndex) => ({
  type: MOVE_REPEATABLE,
  path,
  fromIndex,
  toIndex,
});
export const deleteRepeatable = (path, index) => ({
  type: DELETE_REPEATABLE,
  path,
  index,
});

export const receiveOutline = (outline) => ({
  type: OUTLINE_RECEIVED,
  outline,
});

export const closeEditor = {
  type: EDITOR_CLOSE,
};

export const openEditor = {
  type: EDITOR_OPEN,
};

export const selectTab = (tabIndex) => ({
  type: EDITOR_TAB_SELECT,
  tab: tabIndex,
});

export const updateEditorWidth = (width) => ({
  type: EDITOR_UPDATE_WIDTH,
  width,
});

export const updateEditorHeight = (height) => ({
  type: EDITOR_UPDATE_HEIGHT,
  height,
});

export const updateEditorDock = (dock) => ({
  type: EDITOR_UPDATE_DOCK,
  dock,
});

export const selectComponent = (id) => ({
  type: COMPONENT_SELECTED,
  id,
});

export const updateComponent = (component) => ({
  type: COMPONENT_UPDATED,
  component,
});

export const deleteComponent = (id) => ({
  type: COMPONENT_DELETED,
  id,
});

export const toggleBadge = (componentId) => ({
  type: TOGGLE_BADGE,
  componentId,
});

export const closeAllBadges = {
  type: CLOSE_ALL_BADGES,
};

/**
 * Set the version of the LPC
 * @param {string} version version in string defaults to ""
 */
export const setBuilderVersion = (version = '') => ({
  type: SET_BUILDER_VERSION,
  version,
});

// some components, like the QuerySection, display selectively depending on what query params are set
// this flag determines whether or not we should watch query params for these components when we're in the builder
// so when this flag is false, it indicates that all components should display in the builder for editing purposes
export const watchQueryParamsInBuilder = (watchQueryParams) => ({
  type: SHOULD_WATCH_QUERY_PARAMS,
  watchQueryParams,
});

// for components that selectively render, we may want to override their default state of rendering in the builder and
// force them to render depending on user input
export const alwaysRenderInBuilder = (id) => ({
  type: ALWAYS_RENDER_IN_BUILDER,
  id,
});

/**
 * Copies the given component and inserts the copy directly below the original. Recurses on all children,
 * copying them as well.
 *
 * @param {String} idToCopy - the id of the component that you want to copy
 * @param {String} parentId (optional) - the ID of the parent component for the new copy. defaults to existing parentId
 */
export const copyComponent = (idToCopy, parentId) => (dispatch, getState) => {
  const state = getState();
  const flatOutline = get(state, 'builder.flatOutline');
  const componentToCopy = get(flatOutline, idToCopy, {});
  const type = get(componentToCopy, 'type', null);
  if (isEmpty(componentToCopy) || type === null) return;

  const newId = makeId(componentToCopy);
  // we don't want to pass down children, we copy them explicitly below
  const copiedProps = omit(componentToCopy, 'children');
  if (copiedProps.props) {
    copiedProps.props.isCopy = true;
  }
  const newComponent = {
    ...copiedProps,
    id: newId,
    parentId: parentId || get(componentToCopy, 'parentId', PAGE_ROOT),
  };

  // Add the component
  dispatch(
    addComponent(
      newComponent,
      1 + positionInParent(idToCopy, componentToCopy.parentId, flatOutline)
    )
  );

  // recursively copy all children of this component
  if (componentToCopy.children) {
    componentToCopy.children.forEach((childComponentId) =>
      dispatch(copyComponent(childComponentId, newId))
    );
  }
};

export const loadComponent = (componentType) => {
  const component = componentLibrary[componentType];
  return component && component.preload
    ? component.preload()
    : Promise.resolve('ok');
};

export const insertBelow =
  (siblingId, componentDocument) => (dispatch, getState) => {
    // if there is no component to insert, this should be a no-op
    if (isEmpty(componentDocument)) {
      return Promise.resolve('ok');
    }

    const {
      builder: { flatOutline },
    } = getState();
    const id = makeId(componentDocument);
    const parentId = get(flatOutline, [siblingId, 'parentId']);

    const newComponent = {
      id,
      parentId,
      ...componentDocument,
    };

    dispatch(startLoading('newComponent'));
    return loadComponent(componentDocument.type).then(() => {
      dispatch(
        addComponent(
          newComponent,
          1 + positionInParent(siblingId, parentId, flatOutline)
        )
      );
      dispatch(doneLoading('newComponent'));
    });
  };

export const insertChild = (parentId, componentDocument) => (dispatch) => {
  // if there is no component to insert, this should be a no-op
  if (isEmpty(componentDocument)) {
    return Promise.resolve('ok');
  }

  const id = makeId(componentDocument);
  const newComponent = {
    id,
    parentId,
    ...componentDocument,
  };

  dispatch(startLoading('newComponent'));
  return loadComponent(componentDocument.type).then(() => {
    dispatch(addComponent(newComponent));
    dispatch(doneLoading('newComponent'));
  });
};

export const startSorting = (componentId) => ({
  type: START_SORTING,
  componentId,
});

export const stopSorting = {
  type: STOP_SORTING,
};

export const moveComponent = (movingId, siblingId) => ({
  type: MOVE_COMPONENT,
  movingId,
  siblingId,
});

export const copyToClipboard = (idToCopy, parentId) => (dispatch, getState) => {
  const state = getState();
  const flatOutline = get(state, 'builder.flatOutline');
  const componentToCopy = get(flatOutline, idToCopy, {});
  const type = get(componentToCopy, 'type', null);
  if (isEmpty(componentToCopy) || type === null) return;

  const newId = makeId(componentToCopy);
  const copiedProps = omit(componentToCopy, 'children');
  if (copiedProps.props) {
    copiedProps.props.isCopy = true;
  }

  const newComponent = {
    ...copiedProps,
    id: newId,
    parentId: parentId || get(componentToCopy, 'parentId', PAGE_ROOT),
  };

  navigator.clipboard.writeText(JSON.stringify(newComponent)).then(() => {
    toast.success('Component copied to clipboard!');
  });
};

export const pasteFromClipboard =
  (siblingId = null) =>
  (dispatch, getState) => {
    const state = getState();
    const flatOutline = get(state, 'builder.flatOutline');

    navigator.clipboard.readText().then((data) => {
      const component = JSON.parse(data);
      if (!component.id && !component.parentId) {
        toast.error('Invalid LPC component to copy');
      }
      const newComponent = omit(component, 'children');

      if (!siblingId) {
        dispatch(
          addComponent(newComponent, Object.keys(flatOutline).length - 1)
        );
      } else {
        dispatch(insertBelow(siblingId, newComponent));
      }

      // recursively copy all children of this component
      if (component.children) {
        component.children.forEach((childComponentId) =>
          dispatch(copyComponent(childComponentId, component.id))
        );
      }

      toast.success('Component pasted from clipboard!');
    });
  };
